// announcements-layer/stores/announcements.query-store.ts
import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData, type QueryKey } from '@tanstack/vue-query'

import { AnnouncementsService } from '../services/announcements.service'
import { ANNOUNCEMENTS_PAGE_SIZE } from '../utils/announcement.constants'
import { announcementsQueryKeys } from '../utils/query-keys.utils'
import type {
  AnnouncementDetail,
  AnnouncementListItem,
  AnnouncementListParams,
  AnnouncementListResponse,
  FetchAnnouncementsListQueryOptions,
  FetchAnnouncementsTableQueryOptions,
} from '../types/announcement'

interface MarkAsReadMutationContext {
  previousUnread: Array<[QueryKey, number | undefined]>
  previousAnnouncementCaches: Array<[QueryKey, unknown]>
}

const collectListItemsFromCache = (data: unknown): AnnouncementListItem[] => {
  if (!data || typeof data !== 'object') return []
  if ('pages' in data && Array.isArray((data as InfiniteData<AnnouncementListResponse>).pages)) {
    return (data as InfiniteData<AnnouncementListResponse>).pages.flatMap(page => page.items ?? [])
  }
  if ('items' in data && Array.isArray((data as AnnouncementListResponse).items)) {
    return (data as AnnouncementListResponse).items
  }
  return []
}

const markListItemsRead = (data: unknown, idSet: Set<string>): unknown => {
  if (!data || typeof data !== 'object') return data

  if ('pages' in data && Array.isArray((data as InfiniteData<AnnouncementListResponse>).pages)) {
    const infinite = data as InfiniteData<AnnouncementListResponse>
    return {
      ...infinite,
      pages: infinite.pages.map(page => ({
        ...page,
        items: (page.items ?? []).map(item => (idSet.has(item.id) ? { ...item, isRead: true } : item)),
      })),
    }
  }

  if ('items' in data && Array.isArray((data as AnnouncementListResponse).items)) {
    const page = data as AnnouncementListResponse
    return {
      ...page,
      items: page.items.map(item => (idSet.has(item.id) ? { ...item, isRead: true } : item)),
    }
  }

  return data
}

const countUnreadIdsInCaches = (queryClient: ReturnType<typeof useQueryClient>, location: string, announcementIds: string[]): number => {
  const idSet = new Set(announcementIds)
  const unreadIds = new Set<string>()
  const listPrefix = announcementsQueryKeys.all(location)

  // List/table caches: visibility-tracker and list-driven mark-as-read.
  queryClient
    .getQueriesData({
      queryKey: listPrefix,
      predicate: query => query.queryKey[2] === 'list' || query.queryKey[2] === 'table',
    })
    .forEach(([, data]) => {
      for (const item of collectListItemsFromCache(data)) {
        if (idSet.has(item.id) && !item.isRead) unreadIds.add(item.id)
      }
    })

  // Detail caches: direct deep-links / notification opens never seed list/table,
  // but the detail query is already resolved (isRead:false) when mark-as-read fires.
  queryClient
    .getQueriesData({
      queryKey: listPrefix,
      predicate: query => query.queryKey[2] === 'detail',
    })
    .forEach(([, data]) => {
      const detail = data as AnnouncementDetail | undefined
      if (detail && idSet.has(detail.id) && !detail.isRead) unreadIds.add(detail.id)
    })

  return unreadIds.size
}

/**
 * Optimistically set isRead on list/table/detail caches for the given ids.
 * Returns previous snapshots for rollback on mutation error.
 */
const patchAnnouncementReadStateInCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  location: string,
  announcementIds: string[]
): Array<[QueryKey, unknown]> => {
  const idSet = new Set(announcementIds)
  const previousAnnouncementCaches: Array<[QueryKey, unknown]> = []
  const listPrefix = announcementsQueryKeys.all(location)

  queryClient
    .getQueriesData({
      queryKey: listPrefix,
      predicate: query => query.queryKey[2] === 'list' || query.queryKey[2] === 'table',
    })
    .forEach(([key, data]) => {
      if (!data) return
      previousAnnouncementCaches.push([key, data])
      queryClient.setQueryData(key, markListItemsRead(data, idSet))
    })

  queryClient
    .getQueriesData({
      queryKey: listPrefix,
      predicate: query => query.queryKey[2] === 'detail',
    })
    .forEach(([key, data]) => {
      const detail = data as AnnouncementDetail | undefined
      if (!detail || !idSet.has(detail.id)) return
      previousAnnouncementCaches.push([key, data])
      queryClient.setQueryData(key, { ...detail, isRead: true })
    })

  return previousAnnouncementCaches
}

/**
 * TanStack-Query data layer for announcements. Owns server state only:
 * - infinite list keyed by the resolved filter params
 * - unread count (shared by the banner and the list header)
 * - single announcement detail
 * - mark-as-read mutation
 *
 * Mark-as-read decrements the unread badge optimistically (using unread ids found
 * in list/table *or* detail caches) and patches those rows to isRead:true so a
 * VisibilityTracker remount cannot re-queue the same id. Unread-count is
 * invalidated with refetchType:'active' so cold/deep-link detail opens refresh
 * the badge when list caches were empty and the optimistic delta alone is
 * insufficient.
 */
export const useAnnouncementsQueryStore = (locationId: MaybeRefOrGetter<string>) => {
  const service = new AnnouncementsService()
  const queryClient = useQueryClient()

  const fetchAnnouncementsListQuery = ({ listParams, enabled = true }: FetchAnnouncementsListQueryOptions) => {
    const infiniteQuery = useInfiniteQuery<AnnouncementListResponse, Error>({
      queryKey: computed(() => announcementsQueryKeys.list(toValue(locationId), toValue(listParams))),
      queryFn: ({ pageParam = 1 }) =>
        service.getAnnouncements(toValue(locationId), {
          ...toValue(listParams),
          page: pageParam as number,
          limit: ANNOUNCEMENTS_PAGE_SIZE,
        }),
      getNextPageParam: lastPage => {
        const fetched = (lastPage.page - 1) * lastPage.limit + lastPage.items.length
        return fetched < lastPage.total ? lastPage.page + 1 : undefined
      },
      initialPageParam: 1,
      staleTime: 0,
      enabled: computed(() => !!toValue(locationId) && toValue(enabled)),
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
    })

    const allData = computed<AnnouncementListItem[]>(() => infiniteQuery.data.value?.pages?.flatMap(page => page.items ?? []) ?? [])
    const total = computed<number>(() => infiniteQuery.data.value?.pages?.[0]?.total ?? 0)

    return { ...infiniteQuery, allData, total }
  }

  const fetchAnnouncementsTableQuery = ({ listParams, page, limit, enabled = true }: FetchAnnouncementsTableQueryOptions) => {
    const tableQuery = useQuery<AnnouncementListResponse, Error>({
      queryKey: computed(() => announcementsQueryKeys.tablePage(toValue(locationId), toValue(listParams), toValue(page), toValue(limit))),
      queryFn: () =>
        service.getAnnouncements(toValue(locationId), {
          ...toValue(listParams),
          page: toValue(page),
          limit: toValue(limit),
        }),
      staleTime: 0,
      enabled: computed(() => !!toValue(locationId) && toValue(enabled)),
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
    })

    const items = computed<AnnouncementListItem[]>(() => tableQuery.data.value?.items ?? [])
    const total = computed<number>(() => tableQuery.data.value?.total ?? 0)

    return { ...tableQuery, items, total }
  }

  const fetchUnreadCountQuery = (enabled: MaybeRefOrGetter<boolean> = true) =>
    useQuery<number, Error>({
      queryKey: computed(() => announcementsQueryKeys.unreadCount(toValue(locationId))),
      queryFn: () => service.getUnreadCount(toValue(locationId)),
      staleTime: 60 * 1000,
      enabled: computed(() => !!toValue(locationId) && toValue(enabled)),
      refetchOnWindowFocus: false,
    })

  const fetchAnnouncementDetailQuery = (id: MaybeRefOrGetter<string>) =>
    useQuery<AnnouncementDetail, Error>({
      queryKey: computed(() => announcementsQueryKeys.detail(toValue(locationId), toValue(id))),
      queryFn: () => service.getAnnouncementById(toValue(locationId), toValue(id)),
      enabled: computed(() => !!toValue(locationId) && !!toValue(id)),
      refetchOnWindowFocus: false,
    })

  const createMarkAsReadMutation = () =>
    useMutation<void, Error, string[], MarkAsReadMutationContext>({
      mutationFn: (announcementIds: string[]) => service.markManyAsRead(toValue(locationId), announcementIds),
      onMutate: async (announcementIds: string[]) => {
        const location = toValue(locationId)
        const unreadPrefix = announcementsQueryKeys.unreadCount(location).slice(0, 3)

        await queryClient.cancelQueries({ queryKey: unreadPrefix })

        const previousUnread = queryClient.getQueriesData<number>({ queryKey: unreadPrefix })
        const delta = countUnreadIdsInCaches(queryClient, location, announcementIds)
        const previousAnnouncementCaches = patchAnnouncementReadStateInCaches(queryClient, location, announcementIds)

        queryClient.setQueriesData<number>({ queryKey: unreadPrefix }, current =>
          typeof current === 'number' ? Math.max(0, current - delta) : current
        )

        return { previousUnread, previousAnnouncementCaches }
      },
      onError: (_err, _announcementIds, context) => {
        context?.previousUnread.forEach(([key, value]) => queryClient.setQueryData(key, value))
        context?.previousAnnouncementCaches.forEach(([key, value]) => queryClient.setQueryData(key, value))
      },
      onSuccess: () => {
        // Refresh the active unread badge (detail deep-links may have delta 0
        // until this refetch). List/table/detail isRead already patched in onMutate.
        queryClient.invalidateQueries({
          queryKey: announcementsQueryKeys.unreadCount(toValue(locationId)),
          refetchType: 'active',
        })
      },
    })

  return {
    queryKeys: announcementsQueryKeys,
    fetchAnnouncementsListQuery,
    fetchAnnouncementsTableQuery,
    fetchUnreadCountQuery,
    fetchAnnouncementDetailQuery,
    createMarkAsReadMutation,
  }
}
